// Single Unit Selection — a streamlined 5-step flow for a one-off unit lookup.
//
// Step 1: "mode" — card-based picker between Mode A (max performance sweep) and
//                  Mode B (meet desired load).
// Step 2: "unit" — unit model picker. Mode A: dropdown only. Mode B: dropdown + loads table.
// Step 3: "conditions" — design conditions form (DesignConditionsTab).
// Step 4: "result" — sweep/list results table.
// Step 5: "detail" — RoomReportPage detail view for a clicked row.

import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { ArrowLeft, ArrowUpDown, Loader2, Play, Gauge, FileDown, ChevronUp, ChevronDown, AlertCircle, Eye, EyeOff, BarChart2, Target } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { ALL_MODELS, runRoomSelection, bumpForLength, isPrimaryAir, isModelN } from "../lib/wizardCalc";
import { generateSingleUnitMultiLengthReport } from "../lib/pdfReport";
import DesignConditionsTab from "../components/wizard/DesignConditionsTab";
import RoomDetailsTab, { blankRoom } from "../components/wizard/RoomDetailsTab";
import RoomReportPage from "../components/wizard/RoomReportPage";

// Sensible defaults — same shape as ProjectWizard's initial state, but
// reduced to the fields a one-off selection needs.
const defaultProject = () => ({
  name: "Single Unit Selection",
  number: "",
  designConditions: {
    country: "",
    city: "",
    outdoor_db_f: "95",
    outdoor_wb_f: "78",
    outdoor_db_winter_f: "0",
    altitude_ft: "0",
    room_db_f: "75",
    room_rh: "50",
    room_db_winter_f: "70",
    room_rh_winter: "30",
    water_entering_f: "45",
    water_entering_winter_f: "120",
  },
  advanced: {
    length_ft: 4,
    use_glycol: false,
    glycol_type: "PG",
    glycol_percentage: "",
    o2_circuit: "serial",
    recovery_type: "HRV",
    non_condensing_mode: false,
    water_flow_gpm: "",
    air_on_temp_f: "",
    air_on_temp_heating_f: "",
    pa_eat_db_f: "",
    pa_eat_rh: "",
    pa_flow_cfm: "",
    pa_pressure_in_wg: "",
    maxLengthByModel: {},
    acb_fan_speed: "MED",
  },
});

// Engineering-correct length lists per model family.
// Source: product engineering spec — these are selectable product lengths, NOT coil curve sample points.
// Any model in ALL_MODELS that is absent from this map hits the default [2,3,4,5,6] and logs a warning.
const LENGTHS_BY_MODEL = {
  "Model N":    [3],
  "Model N-f":  [3],
  "Model L-L":  [2, 3, 4, 5, 6],
  "Model L-T":  [2, 3, 4, 5, 6],
  "Model L-F":  [2, 3, 4, 5, 6],
  "Model I-L":  [2, 3, 4, 5, 6],
  "Model I-T":  [2, 3, 4, 5, 6],
  "Model I-F":  [2, 3, 4, 5, 6],
  "Model H":    [2, 3, 4, 5, 6],
  "Model S":    [2, 3, 4, 5, 6],
  "Model S-HW": [2, 3, 4, 5, 6],
  "Model T":    [2, 3, 4, 5, 6],
  "Model C":    [2, 4, 6, 8, 10],
  "Model C1":   [2, 4, 6, 8, 10],
  "Model D":    [2, 4, 6, 8, 10],
  "Model O-1":  [2, 3, 4, 5, 6],
  "Model O-2":  [2, 3, 4, 5, 6],
};

// Concurrency cap for parallel length sweeps (consistent with wizard's pattern).
const MAX_CONCURRENT = 4;

// Run all lengths in parallel with a concurrency cap of MAX_CONCURRENT.
// Returns an array of { length, result } in the same order as `lengths`.
// `oaFlowCfm` is only used for Model N (ERV) where OA must be > 0.
async function runAllLengths(project, model, lengths, baseRoom, oaFlowCfm = 0) {
  const results = new Array(lengths.length).fill(null);
  let idx = 0;

  async function runNext() {
    while (idx < lengths.length) {
      const i = idx++;
      const len = lengths[i];
      const room = {
        ...baseRoom,
        unit_model: model,
        name: `${model} · ${len} ft`,
        cooling_total_btuh: "",
        cooling_sensible_btuh: "",
        heating_btuh: "",
        // Model N (ERV) requires oa_flow_cfm > 0; for chilled-beam models 0 is fine.
        oa_flow_cfm: String(oaFlowCfm),
        ea_flow_cfm: String(oaFlowCfm), // balanced ERV assumption for Model N; 0 for others
        __force_length: len,
      };
      // Pin max-length cap to this length so the engine doesn't shorten the coil.
      const projForRun = {
        ...project,
        advanced: {
          ...project.advanced,
          maxLengthByModel: { ...(project.advanced.maxLengthByModel || {}), [model]: len },
        },
      };
      try {
        const res = await runRoomSelection(projForRun, room);
        results[i] = { length: len, result: res, room };
      } catch (e) {
        results[i] = { length: len, result: { ok: false, error: e?.message || "Error" }, room };
      }
    }
  }

  const workers = Array.from({ length: Math.min(MAX_CONCURRENT, lengths.length) }, runNext);
  await Promise.all(workers);
  return results;
}

// --- Sort helpers ---
function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key] ?? -Infinity;
    const bv = b[key] ?? -Infinity;
    if (av === bv) return 0;
    return dir === "asc" ? (av < bv ? -1 : 1) : (av > bv ? -1 : 1);
  });
}

// Flatten a result's summary into a row for the table.
function toRow(entry) {
  const { length, result } = entry;
  if (!result?.ok) {
    return { length, error: result?.error || "Failed" };
  }
  const s = result.summary || {};
  return {
    length,
    cooling_total: parseFloat(s.cooling_total_btuh) || null,
    cooling_sensible: parseFloat(s.cooling_sensible_btuh) || null,
    cooling_latent: parseFloat(s.cooling_latent_btuh) || null,
    heating: parseFloat(s.heating_btuh) || null,
    airflow: parseFloat(s.air_total_cfm) || parseFloat(s.oa_cfm) || null,
    water_flow: parseFloat(s.water_flow_gpm) || null,
    water_pd: parseFloat(s.water_pd_fth2o) || null,
    leaving_db: parseFloat(s.leaving_air_db_f) || null,
    leaving_wb: parseFloat(s.leaving_air_wb_f) || null,
    error: null,
  };
}

// --- Sort icon component ---
function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="h-3 w-3 ml-1 text-muted-foreground/50" />;
  return dir === "asc"
    ? <ChevronUp className="h-3 w-3 ml-1 text-primary" />
    : <ChevronDown className="h-3 w-3 ml-1 text-primary" />;
}

// --- Numeric cell ---
function Num({ v, digits = 0, em }) {
  if (v === null || v === undefined || isNaN(v)) return <span className="text-muted-foreground">—</span>;
  const formatted = v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return <span className={`tabular-nums font-mono${em ? " font-semibold" : ""}`}>{formatted}</span>;
}

export default function SingleUnitSelection() {
  const { user, loading } = useAuth();
  const [isSI, setIsSI] = useState(false);
  // Step machine: mode | unit | conditions | result | detail
  const [step, setStep] = useState("mode");
  const [project, setProject] = useState(defaultProject);
  const [model, setModel] = useState("");
  const [mode, setMode] = useState(null); // "max" | "target" — null until user picks
  // Mode B: single-row room table (re-use existing RoomDetailsTab + blankRoom).
  const [rooms, setRooms] = useState(() => [blankRoom("")]);
  const [running, setRunning] = useState(false);
  // Mode A: array of { length, result, row } after sweep completes.
  const [sweepRows, setSweepRows] = useState(null);
  // Mode A: selected lengths (Set of ft numbers).
  const [selectedLengths, setSelectedLengths] = useState(new Set());
  // Mode A: sort state.
  const [sortKey, setSortKey] = useState("length");
  const [sortDir, setSortDir] = useState("asc");
  // Mode B: single result — kept for backward compat but no longer used for rendering.
  const [result, setResult] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  // Mode A / B: detail view — stash the clicked sweep entry.
  const [detailEntry, setDetailEntry] = useState(null); // { length, result, room }
  // Mode B: the target total cooling load the engineer entered (BTU/h).
  // Used to compute per-row "meets target" badge. null = no target set.
  const [targetCooling, setTargetCooling] = useState(null);
  // Mode B: when true, hide rows that don't meet target.
  const [filterValidOnly, setFilterValidOnly] = useState(true);

  // Reset sweep state when the user switches modes so stale results don't bleed through.
  useEffect(() => {
    setSweepRows(null);
    setSelectedLengths(new Set());
    setTargetCooling(null);
    setFilterValidOnly(true);
    setResult(null);
  }, [mode]);

  // NOTE: /api/models fetch removed — per-model lengths are now engineering-correct
  // hardcoded constants (LENGTHS_BY_MODEL above), not derived from coil curve sample points.

  const sortedRows = useMemo(() => {
    if (!sweepRows) return [];
    let rows = sweepRows;
    // Mode B filter: hide rows that don't meet target when toggle is ON.
    // Allow 1% tolerance so engine results like achieved=8999 for target=9000 survive the filter.
    if (mode === "target" && filterValidOnly && targetCooling != null && targetCooling > 0) {
      rows = rows.filter((r) => !r.error && (r.cooling_total ?? 0) >= targetCooling * 0.99);
    }
    return sortRows(rows, sortKey, sortDir);
  }, [sweepRows, sortKey, sortDir, mode, filterValidOnly, targetCooling]);

  if (!loading && !user) return <Navigate to="/login" replace />;

  // Resolve the engineering-correct lengths for the selected model.
  // Models not in the map (e.g. Model O-1, O-2, S-HW, I-L) get the [2,3,4,5,6] default.
  const availableLengths = (() => {
    if (LENGTHS_BY_MODEL[model]) return LENGTHS_BY_MODEL[model];
    if (model) console.warn(`No length map for ${model} — using default 2–6 ft`);
    return [2, 3, 4, 5, 6];
  })();

  const updateProject = (patch) => setProject((p) => ({ ...p, ...patch }));

  // Mode B → normal target-driven selection (engine picks best length within
  // user's max-length cap).
  const buildRoomForTarget = () => ({ ...rooms[0], unit_model: model });

  // --- Mode A sweep ---
  const runSweep = async () => {
    if (!model) {
      toast.error("Select a model first");
      return;
    }
    setRunning(true);
    setSweepRows(null);
    setSelectedLengths(new Set());
    try {
      const baseRoom = blankRoom(model);

      // For Model N (ERV), oa_flow_cfm must be > 0 (Pydantic gt=0 constraint).
      // Use the user-entered pa_flow_cfm (which maps to OA for Model N), or fall
      // back to 50 CFM with a toast warning so the sweep doesn't hard-fail.
      let oaFlowCfm = 0;
      if (isModelN(model)) {
        const enteredFlow = parseFloat(project.advanced?.pa_flow_cfm);
        if (enteredFlow > 0) {
          oaFlowCfm = enteredFlow;
        } else {
          oaFlowCfm = 50;
          toast.warning("Defaulting OA to 50 CFM — set a value in Conditions for accurate results");
        }
      }

      const entries = await runAllLengths(project, model, availableLengths, baseRoom, oaFlowCfm);
      const rows = entries.map((e) => ({ ...toRow(e), _entry: e }));
      setSweepRows(rows);
      // Default: select all successful lengths.
      // If exactly 1 length exists, it's auto-selected so "Generate Report" is immediately enabled.
      setSelectedLengths(new Set(rows.filter((r) => !r.error).map((r) => r.length)));
    } catch (e) {
      toast.error("Sweep failed: " + (e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  // --- Mode B: sweep all lengths against the target load ---
  // Each length gets its own bumpForLength call (same water-flow modulation
  // as Mode A's per-length runs) so delivered capacity is accurate per length.
  const runTarget = async () => {
    if (!model) {
      toast.error("Select a model first");
      return;
    }
    const r = rooms[0];
    const total = parseFloat(r.cooling_total_btuh);
    const sens = parseFloat(r.cooling_sensible_btuh);
    if (!(total > 0) && !(sens > 0)) {
      toast.error("Enter a Cooling Total or Sensible load");
      return;
    }
    setRunning(true);
    setSweepRows(null);
    setSelectedLengths(new Set());
    // Store the target so rows can show "meets target" / "% delivered" badges.
    const targetTotal = total > 0 ? total : sens;
    setTargetCooling(targetTotal);
    setFilterValidOnly(true);
    try {
      const room = buildRoomForTarget();

      // Model N only has one length (3 ft) — run a single selection.
      if (isModelN(model)) {
        const res = await runRoomSelection(project, room);
        if (!res.ok) {
          toast.error("Selection failed: " + (res.error || "unknown"));
          return;
        }
        const entry = { length: 3, result: res, room };
        const row = { ...toRow(entry), _entry: entry };
        setSweepRows([row]);
        const meetsTarget = (row.cooling_total ?? 0) >= targetTotal;
        setSelectedLengths(meetsTarget ? new Set([3]) : new Set());
        return;
      }

      // For multi-length models: run bumpForLength for every available length
      // in parallel (capped at MAX_CONCURRENT, same as runAllLengths).
      const lengths = availableLengths;
      const entries = new Array(lengths.length).fill(null);
      let idx = 0;

      async function runNext() {
        while (idx < lengths.length) {
          const i = idx++;
          const len = lengths[i];
          try {
            const out = await bumpForLength(project, room, len);
            if (!out) {
              entries[i] = { length: len, result: { ok: false, error: "No result" }, room };
            } else {
              entries[i] = { length: len, result: { ok: true, raw: out.raw, summary: out.summary }, room };
            }
          } catch (e) {
            entries[i] = { length: len, result: { ok: false, error: e?.message || "Error" }, room };
          }
        }
      }

      const workers = Array.from({ length: Math.min(MAX_CONCURRENT, lengths.length) }, runNext);
      await Promise.all(workers);

      // Sort ascending by length (bumpForLength runs may resolve out of order).
      entries.sort((a, b) => a.length - b.length);

      const rows = entries.map((e) => ({ ...toRow(e), _entry: e }));
      setSweepRows(rows);

      // Default-select only rows that meet or exceed the target.
      const validLengths = new Set(
        rows.filter((row) => !row.error && (row.cooling_total ?? 0) >= targetTotal).map((row) => row.length)
      );
      setSelectedLengths(validLengths);
    } catch (e) {
      toast.error("Selection error: " + (e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  const run = mode === "max" ? runSweep : runTarget;

  // --- Sort ---
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const allSelected = sweepRows
    ? sweepRows.filter((r) => !r.error).every((r) => selectedLengths.has(r.length))
    : false;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedLengths(new Set());
    } else {
      setSelectedLengths(new Set(sweepRows.filter((r) => !r.error).map((r) => r.length)));
    }
  };

  const toggleRow = (len) => {
    setSelectedLengths((prev) => {
      const next = new Set(prev);
      if (next.has(len)) next.delete(len);
      else next.add(len);
      return next;
    });
  };

  // --- Generate report (Mode A multi-length) ---
  const handleGenerateReport = async () => {
    const selected = sortedRows.filter((r) => !r.error && selectedLengths.has(r.length));
    if (!selected.length) return;
    setGeneratingReport(true);
    try {
      // Pass the full entry objects so the PDF generator can render per-length
      // detail pages (html2canvas capture of RoomReportPage for each length).
      const entries = selected.map((r) => r._entry).filter(Boolean);
      await generateSingleUnitMultiLengthReport({
        model,
        project,
        rows: selected,
        entries,
        isSI,
      });
      toast.success("Report generated");
    } catch (e) {
      toast.error("Report failed: " + (e?.message || "unknown"));
    } finally {
      setGeneratingReport(false);
    }
  };

  // Column defs for the results table.
  const cols = [
    { key: "length", label: isSI ? "Length (m)" : "Length (ft)", fmt: (v) => isSI ? `${(v * 0.3048).toFixed(2)} m` : `${v} ft` },
    { key: "cooling_total", label: "Total Cooling (BTU/h)", fmt: (v) => <Num v={v} /> },
    { key: "cooling_sensible", label: "Sensible (BTU/h)", fmt: (v) => <Num v={v} /> },
    { key: "cooling_latent", label: "Latent (BTU/h)", fmt: (v) => <Num v={v} /> },
    { key: "heating", label: "Heating (BTU/h)", fmt: (v) => <Num v={v} /> },
    { key: "airflow", label: "Airflow (CFM)", fmt: (v) => <Num v={v} /> },
    { key: "water_flow", label: "Water Flow (GPM)", fmt: (v) => <Num v={v} digits={2} /> },
    { key: "water_pd", label: "Press. Drop (ft H₂O)", fmt: (v) => <Num v={v} digits={3} /> },
  ];

  // ===== Render Mode A detail view =====
  if (step === "detail" && detailEntry) {
    const { length: detailLength, result: detailResult, room: detailRoom } = detailEntry;
    const isSI_label = isSI
      ? `${(detailLength * 0.3048).toFixed(2)} m`
      : `${detailLength} ft`;
    return (
      <div className="container mx-auto py-6 px-4 xl:px-10 max-w-[1920px]" data-testid="single-detail">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setStep("result")}
            data-testid="single-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to results
          </Button>
          <div className="text-sm text-muted-foreground">
            Length: {isSI_label} · {model}
          </div>
          <Link to="/home">
            <Button variant="ghost" size="sm">Home</Button>
          </Link>
        </div>
        <RoomReportPage
          project={project}
          room={detailRoom}
          result={detailResult}
          isSI={isSI}
          showChart
          onBack={() => setStep("result")}
          lockedLength={detailLength}
        />
      </div>
    );
  }

  const nSelected = selectedLengths.size;
  const nTotal = sweepRows ? sweepRows.filter((r) => !r.error).length : 0;

  // Tab navigation helpers — enforce forward-only unlock by state.
  // "unit" and beyond require a mode to be chosen; "conditions" requires a model.
  const unitUnlocked = mode !== null;
  const conditionsUnlocked = unitUnlocked && !!model;
  const resultUnlocked = conditionsUnlocked && sweepRows !== null;

  // Pre-populate the Conditions tab's OA Flow field from the room table's
  // OA Flow value when the user advances into the Conditions step in Mode B
  // with Model N. The two fields serve the same engineering value; this
  // avoids double entry. One-shot copy at transition — further edits in the
  // Conditions tab are preserved until the user returns to the Unit step.
  const goToConditions = () => {
    if (mode === "target" && isModelN(model)) {
      const roomOa = rooms[0]?.oa_flow_cfm;
      if (roomOa && roomOa !== project.advanced?.pa_flow_cfm) {
        setProject((p) => ({
          ...p,
          advanced: { ...p.advanced, pa_flow_cfm: String(roomOa) },
        }));
      }
    }
    setStep("conditions");
  };

  const handleTabChange = (next) => {
    if (next === "conditions") {
      goToConditions();
    } else {
      setStep(next);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 xl:px-10 max-w-[1920px]" data-testid="single-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to="/home">
            <Button variant="ghost" size="sm" data-testid="single-back-home">
              <ArrowLeft className="h-4 w-4 mr-1" /> Home
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" /> Single Unit Selection
            </h1>
            <p className="text-sm text-muted-foreground">Quick selection for one unit — no project save.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={isSI ? "text-muted-foreground" : "font-semibold text-foreground"}>IP</span>
          <button
            onClick={() => setIsSI(!isSI)}
            className={`relative h-5 w-9 rounded-full transition-colors ${isSI ? "bg-primary" : "bg-muted"}`}
            data-testid="single-unit-toggle"
            aria-label="Toggle units"
          >
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-background transition-all ${isSI ? "left-4" : "left-0.5"}`} />
          </button>
          <span className={isSI ? "font-semibold text-foreground" : "text-muted-foreground"}>SI</span>
        </div>
      </div>

      <Tabs value={step} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="mode" data-testid="single-tab-mode">1. Mode</TabsTrigger>
          <TabsTrigger value="unit" data-testid="single-tab-unit" disabled={!unitUnlocked}>
            2. Unit
          </TabsTrigger>
          <TabsTrigger value="conditions" data-testid="single-tab-conditions" disabled={!conditionsUnlocked}>
            3. Conditions
          </TabsTrigger>
          <TabsTrigger value="result" data-testid="single-tab-result" disabled={!resultUnlocked}>
            4. Results
          </TabsTrigger>
        </TabsList>

        {/* ===== Step 1 : Mode picker ===== */}
        <TabsContent value="mode" className="mt-4" data-testid="single-mode-picker">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mode A card */}
            <button
              type="button"
              data-testid="single-mode-max-card"
              onClick={() => {
                setMode("max");
                setStep("unit");
              }}
              className={[
                "text-left rounded border p-5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                mode === "max"
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border bg-card hover:border-primary/60 hover:bg-muted/50",
              ].join(" ")}
              aria-pressed={mode === "max"}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full ${mode === "max" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  <BarChart2 className="h-5 w-5" />
                </span>
                <span className="font-semibold text-base">Find max performance</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Engine reports maximum capacity at max water flow for every available length.
                Results shown as a sortable sweep table — no load targets needed.
              </p>
            </button>

            {/* Mode B card */}
            <button
              type="button"
              data-testid="single-mode-target-card"
              onClick={() => {
                setMode("target");
                setStep("unit");
              }}
              className={[
                "text-left rounded border p-5 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                mode === "target"
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border bg-card hover:border-primary/60 hover:bg-muted/50",
              ].join(" ")}
              aria-pressed={mode === "target"}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className={`flex h-9 w-9 items-center justify-center rounded-full ${mode === "target" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                  <Target className="h-5 w-5" />
                </span>
                <span className="font-semibold text-base">Meet desired load</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Enter cooling / heating / airflow targets — engine sizes coil and water flow per length.
                Results show which lengths meet the load.
              </p>
            </button>
          </div>
        </TabsContent>

        {/* ===== Step 2 : Unit picker ===== */}
        <TabsContent value="unit" className="mt-4" data-testid="single-unit-step">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {mode === "max" ? "Select Unit Model" : "Select Unit Model & Load Targets"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Model dropdown — always shown */}
              <div className="flex items-end gap-4">
                <div>
                  <Label className="text-sm mb-1 block">Unit Model</Label>
                  <Select value={model} onValueChange={(v) => {
                    setModel(v);
                    // Sync model into the room row for Mode B
                    setRooms([{ ...rooms[0], unit_model: v }]);
                  }}>
                    <SelectTrigger className="w-56" data-testid="single-model">
                      <SelectValue placeholder="Select a model…" />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_MODELS.map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {mode === "max" && model && (
                  <p className="text-sm text-muted-foreground pb-1">
                    {availableLengths.length} length{availableLengths.length !== 1 ? "s" : ""} will be swept: {availableLengths.map((l) => `${l} ft`).join(", ")}
                  </p>
                )}
              </div>

              {/* Mode B: loads table — hidden for Mode A */}
              {mode === "target" && model && (
                <RoomDetailsTab
                  rooms={rooms.slice(0, 1).map((r) => ({ ...r, unit_model: model }))}
                  setRooms={(updater) => {
                    const next = typeof updater === "function" ? updater(rooms) : updater;
                    // Lock to a single row.
                    setRooms([next[0] || rooms[0]]);
                  }}
                  isSI={isSI}
                  hideCapacityColumns={false}
                />
              )}

              {/* Mode A: just the model dropdown — show a placeholder table row for visual clarity */}
              {mode === "max" && model && (
                <div className="rounded border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
                  No load targets needed in <strong>Find max performance</strong> mode — the engine will run at maximum capacity for each length.
                </div>
              )}

              <div className="pt-2 border-t flex justify-between">
                <Button variant="outline" onClick={() => setStep("mode")} data-testid="single-unit-back-mode">
                  ← Back
                </Button>
                <Button
                  onClick={goToConditions}
                  disabled={!model}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="single-next-conditions"
                >
                  Next: Conditions →
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Step 3 : Design + Advanced + Run ===== */}
        <TabsContent value="conditions" className="mt-4 space-y-4">
          <DesignConditionsTab
            project={project}
            updateProject={updateProject}
            rooms={rooms.map((r) => ({ ...r, unit_model: model }))}
            isSI={isSI}
            paFlowTestId="conditions-pa-flow-cfm"
            hideLengthInputs={mode === "max"}
          />
          <div className="flex items-center justify-between sticky bottom-0 bg-background border-t border-border py-3 -mx-4 px-4">
            <Button
              variant="outline"
              onClick={() => setStep("unit")}
              data-testid="single-back-setup"
            >
              ← Back to Unit
            </Button>
            <Button
              onClick={async () => {
                await run();
                setStep("result");
              }}
              disabled={running}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="single-run-btn"
            >
              {running ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              {running
                ? (mode === "max" ? "Running all lengths…" : "Running…")
                : (mode === "max" ? `Run all lengths (${availableLengths.length})` : "Run Selection")}
            </Button>
          </div>
        </TabsContent>

        {/* ===== Step 4 : Results ===== */}
        <TabsContent value="result" className="mt-4">
          {sweepRows === null && !running && (
            <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Go back to Conditions and click <strong>Run</strong> to generate results.
            </div>
          )}

          {sweepRows !== null && (
            <div className="space-y-3" data-testid="single-results-table">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-muted-foreground">
                    {nSelected} of {nTotal} length{nTotal !== 1 ? "s" : ""} selected
                  </span>
                  {/* Mode B: filter toggle */}
                  {mode === "target" && targetCooling != null && (
                    <button
                      onClick={() => setFilterValidOnly((v) => !v)}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="single-filter-toggle"
                      aria-pressed={filterValidOnly}
                    >
                      {filterValidOnly
                        ? <Eye className="h-3.5 w-3.5" />
                        : <EyeOff className="h-3.5 w-3.5" />}
                      {filterValidOnly ? "Showing valid lengths only" : "Showing all lengths"}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStep("conditions")}
                    data-testid="single-result-back-btn"
                  >
                    ← Back to Conditions
                  </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={nSelected === 0 ? 0 : -1}>
                          <Button
                            size="sm"
                            onClick={handleGenerateReport}
                            disabled={nSelected === 0 || generatingReport}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground"
                            data-testid="single-generate-report-btn"
                          >
                            {generatingReport ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <FileDown className="h-4 w-4 mr-2" />
                            )}
                            Generate Report
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {nSelected === 0 && (
                        <TooltipContent>Select at least one length to include in the report.</TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              {/* Table */}
              <div className="rounded-md border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-10 px-3">
                        <Checkbox
                          checked={allSelected && nTotal > 0}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all lengths"
                          data-testid="single-select-all"
                        />
                      </TableHead>
                      {cols.map((col) => (
                        <TableHead
                          key={col.key}
                          className="cursor-pointer select-none whitespace-nowrap text-xs"
                          onClick={() => handleSort(col.key)}
                        >
                          <span className="flex items-center">
                            {col.label}
                            <SortIcon active={sortKey === col.key} dir={sortDir} />
                          </span>
                        </TableHead>
                      ))}
                      {/* Mode B: status badge column */}
                      {mode === "target" && targetCooling != null && (
                        <TableHead className="whitespace-nowrap text-xs">Status</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedRows.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={cols.length + 1 + (mode === "target" && targetCooling != null ? 1 : 0)}
                          className="text-center text-muted-foreground py-8"
                        >
                          {mode === "target" && filterValidOnly
                            ? "No lengths within 1% of target — toggle filter to see all."
                            : "No results"}
                        </TableCell>
                      </TableRow>
                    )}
                    {sortedRows.map((row) => {
                      // Mode B: per-row capacity status (tri-state)
                      // meetsTarget: full meet | withinTolerance: within 1% under | underTarget: >1% short
                      const cooling = row.cooling_total ?? 0;
                      const meetsTarget =
                        mode === "target" && targetCooling != null && !row.error
                          ? cooling >= targetCooling
                          : null;
                      const withinTolerance =
                        mode === "target" && targetCooling != null && !row.error && !meetsTarget
                          ? cooling >= targetCooling * 0.99
                          : false;
                      const pctDelivered =
                        mode === "target" && targetCooling != null && !row.error && !meetsTarget && !withinTolerance
                          ? Math.round((cooling / targetCooling) * 100)
                          : null;

                      return (
                        <TableRow
                          key={row.length}
                          data-testid={`single-result-row-${row.length}`}
                          className={[
                            selectedLengths.has(row.length) && !row.error ? "bg-primary/5" : "",
                            !row.error ? "cursor-pointer hover:bg-muted/50" : "",
                          ].filter(Boolean).join(" ")}
                          onClick={() => {
                            if (row.error) return;
                            const entry = sweepRows.find((r) => r.length === row.length)?._entry;
                            if (!entry) return;
                            setDetailEntry(entry);
                            setStep("detail");
                          }}
                        >
                          <TableCell
                            className="px-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.error ? (
                              <AlertCircle className="h-4 w-4 text-destructive" title={String(row.error)} />
                            ) : (
                              <Checkbox
                                checked={selectedLengths.has(row.length)}
                                onCheckedChange={() => toggleRow(row.length)}
                                aria-label={`Select ${row.length} ft`}
                              />
                            )}
                          </TableCell>
                          {cols.map((col) => (
                            <TableCell key={col.key} className="text-sm py-2">
                              {row.error && col.key !== "length"
                                ? (col.key === "cooling_total" ? (
                                  <span className="text-xs text-destructive">{String(row.error)}</span>
                                ) : "")
                                : col.fmt(row[col.key])}
                            </TableCell>
                          ))}
                          {/* Mode B status badge */}
                          {mode === "target" && targetCooling != null && (
                            <TableCell className="py-2">
                              {!row.error && meetsTarget && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                  Meets target
                                </span>
                              )}
                              {!row.error && withinTolerance && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                  Within 1% (achieved {Math.round(cooling).toLocaleString()} BTU/h)
                                </span>
                              )}
                              {!row.error && pctDelivered !== null && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                  Under target — {pctDelivered}% delivered
                                </span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
